/**
 * Icon Columns Carousel Component
 * Horizontal scroll carousel with auto-scroll for mobile devices
 */

(function() {
  // Prevent re-declaration if script is loaded multiple times
  if (customElements.get('icon-columns-carousel')) return;

  class IconColumnsCarousel extends HTMLElement {
    constructor() {
      super();
      
      this._currentIndex = 0;
      this._autoScrollInterval = null;
      this._userInteractionTimeout = null;
      this._isUserInteracting = false;
      
      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.autoScroll = this.autoScroll.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
    }

    connectedCallback() {
      this.wrapper = this.querySelector('.icon-columns__wrapper');
      this.items = Array.from(this.querySelectorAll('.icon-columns__item'));
      this.dots = Array.from(this.querySelectorAll('.icon-columns-carousel__dot'));
      
      if (!this.wrapper || this.items.length <= 1) return;
      
      this.setupEventListeners();
      this.checkIfMobile();
      this.updateDots();
      
      // Start auto-scroll if on mobile and enabled
      if (this.isMobile && this.autoplayEnabled) {
        this.startAutoScroll();
      }
    }

    disconnectedCallback() {
      this.stopAutoScroll();
      this.removeEventListeners();
    }

    get autoplayEnabled() {
      return this.hasAttribute('autoplay');
    }

    get autoplaySpeed() {
      const speed = parseInt(this.getAttribute('autoplay-speed'), 10);
      return isNaN(speed) ? 4000 : speed * 1000;
    }

    get isMobile() {
      return window.innerWidth < 750;
    }

    setupEventListeners() {
      this.wrapper.addEventListener('scroll', this.handleScroll, { passive: true });
      this.wrapper.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.wrapper.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      this.wrapper.addEventListener('mouseenter', () => this.pauseAutoScroll());
      this.wrapper.addEventListener('mouseleave', () => this.resumeAutoScroll());
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('resize', this.handleResize);
      
      // Dot click handlers
      this.dots.forEach(dot => {
        dot.addEventListener('click', this.handleDotClick);
      });
    }

    removeEventListeners() {
      if (this.wrapper) {
        this.wrapper.removeEventListener('scroll', this.handleScroll);
        this.wrapper.removeEventListener('touchstart', this.handleTouchStart);
        this.wrapper.removeEventListener('touchend', this.handleTouchEnd);
      }
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('resize', this.handleResize);
      
      this.dots.forEach(dot => {
        dot.removeEventListener('click', this.handleDotClick);
      });
    }

    checkIfMobile() {
      const wasMobile = this._wasMobile;
      this._wasMobile = this.isMobile;
      
      if (this.isMobile && this.autoplayEnabled) {
        if (!wasMobile) {
          this.startAutoScroll();
        }
      } else {
        this.stopAutoScroll();
      }
    }

    handleResize() {
      this.checkIfMobile();
    }

    handleScroll() {
      if (!this.isMobile) return;
      
      const scrollLeft = this.wrapper.scrollLeft;
      const itemWidth = this.items[0]?.offsetWidth || 0;
      
      if (itemWidth > 0) {
        const newIndex = Math.round(scrollLeft / itemWidth);
        if (newIndex !== this._currentIndex) {
          this._currentIndex = newIndex;
          this.updateDots();
        }
      }
    }

    handleTouchStart() {
      this._isUserInteracting = true;
      this.pauseAutoScroll();
    }

    handleTouchEnd() {
      this._isUserInteracting = false;
      
      // Resume auto-scroll after user stops interacting (with delay)
      clearTimeout(this._userInteractionTimeout);
      this._userInteractionTimeout = setTimeout(() => {
        this.resumeAutoScroll();
      }, 3000); // 3 second delay after user interaction
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoScroll();
      } else {
        this.resumeAutoScroll();
      }
    }

    handleDotClick(event) {
      const dot = event.currentTarget;
      const index = parseInt(dot.dataset.index, 10);
      
      if (!isNaN(index)) {
        this._isUserInteracting = true;
        this.pauseAutoScroll();
        this.scrollToIndex(index);
        
        // Resume auto-scroll after delay
        clearTimeout(this._userInteractionTimeout);
        this._userInteractionTimeout = setTimeout(() => {
          this._isUserInteracting = false;
          this.resumeAutoScroll();
        }, 3000);
      }
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-selected', index === this._currentIndex ? 'true' : 'false');
      });
    }

    startAutoScroll() {
      if (!this.isMobile || !this.autoplayEnabled || this.items.length <= 1) return;
      
      this.stopAutoScroll();
      this._autoScrollInterval = setInterval(this.autoScroll, this.autoplaySpeed);
    }

    stopAutoScroll() {
      if (this._autoScrollInterval) {
        clearInterval(this._autoScrollInterval);
        this._autoScrollInterval = null;
      }
    }

    pauseAutoScroll() {
      this.stopAutoScroll();
    }

    resumeAutoScroll() {
      if (!this._isUserInteracting && this.isMobile && this.autoplayEnabled) {
        this.startAutoScroll();
      }
    }

    autoScroll() {
      if (!this.isMobile || !this.wrapper || this.items.length === 0) return;
      
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }
      
      const nextIndex = (this._currentIndex + 1) % this.items.length;
      this.scrollToIndex(nextIndex);
    }

    scrollToIndex(index) {
      if (!this.wrapper || !this.items[index]) return;
      
      const item = this.items[index];
      const scrollLeft = item.offsetLeft - (this.wrapper.offsetWidth - item.offsetWidth) / 2;
      
      this.wrapper.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
      
      this._currentIndex = index;
      this.updateDots();
    }
  }

  // Register custom element
  customElements.define('icon-columns-carousel', IconColumnsCarousel);

  // Shopify theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('icon-columns-carousel');
      if (section) {
        section.connectedCallback();
      }
    });
  }
})();
